import { createFileRoute, Link } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { ArrowRight, Send } from 'lucide-react'
import { StartFreeButton } from '@/components/marketing/free-plan-cta'
import { useFreePlan } from '@/hooks/use-free-plan'
import { chatFn, type ChatMessage } from '@/server/chat'

export const Route = createFileRoute('/_site/chat')({
  component: ChatPage,
  head: () => ({
    meta: [
      { title: 'Ask Uptrack — Product Assistant' },
      { name: 'description', content: 'Ask the Uptrack assistant about features, pricing, integrations, and how Uptrack compares to other monitoring tools. No signup required.' },
      { property: 'og:title', content: 'Ask Uptrack — Product Assistant' },
      { property: 'og:description', content: 'Get instant answers about Uptrack features, pricing, and integrations.' },
      { property: 'og:image', content: 'https://uptrack.app/og/chat.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'robots', content: 'index, follow' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/chat' },
    ],
  }),
})

const SUGGESTIONS = [
  'Which plan is right for me?',
  'How does Uptrack compare to UptimeRobot?',
  'What monitor types do you support?',
  'How does alert confirmation work?',
]

export function ChatPage() {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [draft, setDraft] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [intent, setIntent] = useState<'lead' | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const free = useFreePlan()

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages.length, pending])

  async function send(text: string) {
    const trimmed = text.trim()
    if (!trimmed || pending) return

    const next: ChatMessage[] = [...messages, { role: 'user', content: trimmed }]
    setMessages(next)
    setDraft('')
    setPending(true)
    setError(null)

    try {
      const out = await chatFn({ data: { messages: next } })
      setMessages([...next, { role: 'assistant', content: out.reply }])
      setIntent(out.intent ?? null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong')
    } finally {
      setPending(false)
    }
  }

  return (
        <section className="container mx-auto max-w-3xl px-4 py-12 md:py-16">
          <div className="mb-8">
            <h1 className="text-[2.25rem] font-semibold leading-[1.1] tracking-tight md:text-[3rem]">Ask Uptrack</h1>
            <p className="mt-4 max-w-2xl text-lead text-body">
              Questions about features, pricing, or how Uptrack compares to other tools? Ask away — no signup required.
            </p>
          </div>

          <div
            ref={scrollRef}
            className="mb-4 max-h-[60vh] overflow-y-auto rounded-lg border border-border bg-card p-4"
          >
            {messages.length === 0 && !pending ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">Try one of these:</p>
                <div className="flex flex-wrap gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => void send(s)}
                      className="rounded-md border border-border bg-muted px-3 py-2 text-sm font-medium transition-colors hover:border-primary hover:text-primary"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <ul className="space-y-4">
                {messages.map((m, i) => (
                  <li
                    key={i}
                    className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-lg px-4 py-2.5 text-sm leading-relaxed ${
                        m.role === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : 'border border-border bg-background'
                      }`}
                    >
                      {m.role === 'assistant' ? (
                        <MarkdownLite text={m.content} />
                      ) : (
                        m.content
                      )}
                    </div>
                  </li>
                ))}
                {pending && (
                  <li className="flex justify-start">
                    <div className="rounded-lg border border-border bg-background px-4 py-2.5 text-sm text-muted-foreground">
                      <TypingDots />
                    </div>
                  </li>
                )}
              </ul>
            )}
          </div>

          {error && (
            <div className="mb-3 rounded-md border border-status-down bg-status-down-soft px-3 py-2 text-sm text-status-down">
              {error}
            </div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault()
              void send(draft)
            }}
            className="flex gap-2"
          >
            <input
              type="text" aria-label="Ask about Uptrack"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Ask about features, pricing, integrations…"
              maxLength={2000}
              disabled={pending}
              className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-base focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
            />
            <Button type="submit" disabled={pending || draft.trim().length === 0}>
              <Send className="h-4 w-4" />
              <span className="sr-only">Send</span>
            </Button>
          </form>

          {intent === 'lead' && messages.length > 0 && (
            <div className="mt-6 rounded-lg border border-border bg-muted p-4">
              <p className="mb-3 text-sm font-medium">Ready to try Uptrack?</p>
              <p className="mb-4 text-sm text-body">
                {free.isOpen
                  ? 'Start free, or choose Pro, Team, or Business for more monitors and locations.'
                  : 'Choose Pro, Team, or Business for 30-second monitoring and alerts.'}
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <StartFreeButton />
                <Button size="lg" variant={free.isOpen ? 'outline' : 'default'} className="h-11 px-5 text-[15px]" asChild>
                  <Link to="/pricing">
                    See plans
                    <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
                  </Link>
                </Button>
              </div>
            </div>
          )}

          <p className="mt-6 text-sm text-muted-foreground">
            Answers are AI-generated from public Uptrack documentation. For complex questions, email <a href="mailto:hello@uptrack.app" className="underline">hello@uptrack.app</a>.
          </p>
        </section>
  )
}

function TypingDots() {
  return (
    <span className="inline-flex gap-1">
      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current [animation-delay:-0.3s]" />
      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current [animation-delay:-0.15s]" />
      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current" />
    </span>
  )
}

/**
 * Where a link in an answer may go: a path on this site, https, or mailto. Anything else
 * (javascript:, data:, vbscript:, a protocol-relative //host) is not a link. The model writes
 * the answer, so its links are input, not code.
 */
function linkTarget(raw: string): { href: string; external: boolean } | null {
  const href = raw.trim()
  if (href.startsWith('/')) return href.startsWith('//') || href.startsWith('/\\') ? null : { href, external: false }
  if (/^https:\/\/[^\s]+$/i.test(href) || /^mailto:[^\s]+$/i.test(href)) return { href, external: true }
  return null
}

export function MarkdownLite({ text }: { text: string }) {
  const parts = text.split(/(\[[^\]]+\]\([^)]+\))/g)
  return (
    <span className="whitespace-pre-wrap">
      {parts.map((part, i) => {
        const m = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/)
        if (m) {
          const [, label, raw] = m
          const target = linkTarget(raw)
          if (!target) return <span key={i}>{label}</span>
          return (
            <a
              key={i}
              href={target.href}
              {...(target.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              className="text-primary underline"
            >
              {label}
            </a>
          )
        }
        return <span key={i}>{part}</span>
      })}
    </span>
  )
}
